import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import EmployeeDocumentModal from "../components/EmployeeDocumentModal";
import { api, employeesAPI, organigrammeAPI, API_BASE_URL, documentsAPI, employeeFiltersAPI } from "../services/api";
import OrganigrammeTree from "../components/OrganigrammeTree";
import ImportEmployeesModal from "../components/ImportEmployeesModal";
import { getEmptyFilterGroup, ensureFilterGroup, isGroupEmpty } from "../utils/filterUtils";
import useEmployeeForm from "../hooks/useEmployeeForm";
import useEmployeeColumns from "../hooks/useEmployeeColumns";
import EmployeeFormModal from "../components/employee/EmployeeFormModal";
import AdvancedFiltersDialog from "../components/employee/AdvancedFiltersDialog";
import EmployeeStatsAccordion from "../components/employee/EmployeeStatsAccordion";
import EmployeeTableCard from "../components/employee/EmployeeTableCard";
import EmployeesHeader from "../components/employee/EmployeesHeader";
import EmployeesSearchCard from "../components/employee/EmployeesSearchCard";
import EmployeeDeleteDialog from "../components/employee/EmployeeDeleteDialog";
import { renderCellContent } from "../utils/employeeCellRenderer";

const toUploadUrl = (path) => {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return API_BASE_URL.startsWith('http') ? `${API_BASE_URL}${clean}` : clean;
};

const Employees = () => {
  const { t, i18n } = useTranslation();

  // ── Columns (via hook) ──────────────────────────────────
  const { visibleColumns, toggleColumnVisibility, getVisibleColumnDefs, getColumnLabel } = useEmployeeColumns();

  // ── Employee form (via hook) ────────────────────────────
  const { formData, setFormData, updateField, resetForm, populateForm, photoPreview, setPhotoPreview } = useEmployeeForm();

  const queryClient = useQueryClient();
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [mutating, setMutating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterService, setFilterService] = useState("");
  const [selectedDepartement, setSelectedDepartement] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [departements, setDepartements] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showSuspended, setShowSuspended] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sites, setSites] = useState([]);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [documentTemplatesLoading, setDocumentTemplatesLoading] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [advancedFiltersDialogOpen, setAdvancedFiltersDialogOpen] = useState(false);
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(getEmptyFilterGroup());
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(getEmptyFilterGroup());
  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState(null);
  const [newFilterName, setNewFilterName] = useState("");
  const [newFilterDescription, setNewFilterDescription] = useState("");
  const [savingFilter, setSavingFilter] = useState(false);

  const loadDocumentTemplates = useCallback(async () => {
    setDocumentTemplatesLoading(true);
    try {
      const templates = await documentsAPI.listTemplates();
      const normalized = Array.isArray(templates) ? templates : [];
      setDocumentTemplates(normalized);
      return normalized;
    } catch (error) {
      toast.error(t("extra.empl.docTemplatesError"));
      setDocumentTemplates([]);
      return [];
    } finally {
      setDocumentTemplatesLoading(false);
    }
  }, []);

  const loadSavedFilters = useCallback(async () => {
    setSavedFiltersLoading(true);
    try {
      const filters = await employeeFiltersAPI.list();
      const normalized = Array.isArray(filters) ? filters : [];
      setSavedFilters(normalized);
      return normalized;
    } catch (error) {
      toast.error(t("extra.empl.savedFiltersError"));
      setSavedFilters([]);
      return [];
    } finally {
      setSavedFiltersLoading(false);
    }
  }, []);

  const employeesQuery = useQuery({
    queryKey: ['employees', selectedDepartement?.id ?? null, isGroupEmpty(appliedAdvancedFilters) ? null : appliedAdvancedFilters],
    queryFn: async () => {
      const params = { limit: 1000 };
      if (selectedDepartement?.id) params.department_id = selectedDepartement.id;
      if (!isGroupEmpty(appliedAdvancedFilters)) params.filters = appliedAdvancedFilters;
      const data = await employeesAPI.getEmployees(params);
      return data?.employees || [];
    },
    staleTime: 30_000,
  });

  const employees = employeesQuery.data ?? [];
  const loading = employeesQuery.isPending || mutating;

  useEffect(() => {
    if (employeesQuery.isError) {
      toast.error(t("extra.empl.employeesLoadError"));
    }
  }, [employeesQuery.isError]);


  const renderCell = (employee, columnId) => renderCellContent(employee, columnId, {
    selectedEmployees, setSelectedEmployees, setShowBulkActions,
    handleEdit, setSelectedEmployee, setShowDeleteDialog, t, language: i18n.language,
  });

  useEffect(() => {
    loadDepartements();
    loadSites();
  }, []);

  useEffect(() => {
    loadDocumentTemplates();
    loadSavedFilters();
  }, [loadDocumentTemplates, loadSavedFilters]);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchTerm, filterService, selectedDepartement, showInactive, showSuspended, showArchived]);

  const loadDepartements = async () => {
    try {
      const response = await organigrammeAPI.getOrganizationChart();
      const buildHierarchicalPath = (deps, parentPath = "") => {
        let result = [];
        deps.forEach((dep) => {
          const currentPath = parentPath ? `${parentPath} > ${dep.nom}` : dep.nom;
          result.push({
            ...dep,
            niveau: parentPath ? parentPath.split(" > ").length : 0,
            nom_complet: currentPath,
            nom_avec_indentation: "  ".repeat(parentPath ? parentPath.split(" > ").length : 0) + dep.nom,
            chemin_hierarchique: currentPath,
          });
          if (dep.enfants && dep.enfants.length > 0) {
            result = result.concat(buildHierarchicalPath(dep.enfants, currentPath));
          }
        });
        return result;
      };
      const flatDepartements = buildHierarchicalPath(response.organization_chart);
      setDepartements(flatDepartements);
    } catch (error) {
    }
  };

  const loadSites = async () => {
    try {
      const response = await api.get('v1/sites/');
      const sitesData = Array.isArray(response.data) ? response.data : (response.data?.sites || []);
      setSites(sitesData);
    } catch (error) {
      setSites([]);
    }
  };

  const filterEmployees = () => {
    if (!Array.isArray(employees)) {
      setFilteredEmployees([]);
      return;
    }
    let list = [...employees];
    if (selectedDepartement) {
      list = list.filter((emp) => emp.department_id === selectedDepartement.id);
    }
    if (!showInactive) list = list.filter((emp) => emp.status !== "INACTIVE");
    if (!showSuspended) list = list.filter((emp) => emp.status !== "SUSPENDED");
    if (!showArchived) list = list.filter((emp) => emp.status !== "ARCHIVED");
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (emp) =>
          emp.full_name?.toLowerCase().includes(term) ||
          emp.email?.toLowerCase().includes(term) ||
          emp.job_name?.toLowerCase().includes(term)
      );
    }
    setFilteredEmployees(list);
  };

  const hasAdvancedFilters = useMemo(() => !isGroupEmpty(appliedAdvancedFilters), [appliedAdvancedFilters]);

  const employeesMap = useMemo(() => {
    const map = {};
    employees.forEach((employee) => { map[employee.id] = employee; });
    return map;
  }, [employees]);

  const documentTargetIds = useMemo(() => {
    if (selectedEmployees.length > 0) return Array.from(new Set(selectedEmployees));
    return filteredEmployees.map((employee) => employee.id);
  }, [filteredEmployees, selectedEmployees]);

  const handleOpenAdvancedFilters = () => {
    setDraftAdvancedFilters(appliedAdvancedFilters);
    setAdvancedFiltersDialogOpen(true);
  };

  const handleApplyAdvancedFilters = () => {
    const normalized = ensureFilterGroup(draftAdvancedFilters);
    setAppliedAdvancedFilters(normalized);
    setAdvancedFiltersDialogOpen(false);
  };

  const handleResetAdvancedFilters = () => {
    const empty = getEmptyFilterGroup();
    setDraftAdvancedFilters(empty);
    setAppliedAdvancedFilters(empty);
    setSelectedSavedFilterId(null);
  };

  const handleSaveFilter = async () => {
    const normalized = ensureFilterGroup(draftAdvancedFilters);
    if (isGroupEmpty(normalized)) {
      toast.info(t("extra.empl.addRuleFirst"));
      return;
    }
    if (!newFilterName.trim()) {
      toast.info(t("extra.empl.nameYourFilter"));
      return;
    }
    try {
      setSavingFilter(true);
      await employeeFiltersAPI.create({
        name: newFilterName.trim(),
        description: newFilterDescription.trim() || undefined,
        filters: normalized,
        visible_columns: visibleColumns,
      });
      toast.success(t("extra.empl.filterSaved"));
      setNewFilterName("");
      setNewFilterDescription("");
      await loadSavedFilters();
    } catch (error) {
      toast.error("Impossible d'enregistrer ce filtre.");
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await employeeFiltersAPI.delete(filterId);
      if (selectedSavedFilterId === filterId) setSelectedSavedFilterId(null);
      await loadSavedFilters();
      toast.success(t("extra.empl.filterDeleted"));
    } catch (error) {
      toast.error("Impossible de supprimer ce filtre.");
    }
  };

  const handleSelectSavedFilter = (filter) => {
    setSelectedSavedFilterId(filter.id);
    setDraftAdvancedFilters(ensureFilterGroup(filter.filters));
  };

  const handleOpenDocumentsModal = async () => {
    if (documentTargetIds.length === 0) {
      toast.info(t("extra.empl.selectToPrint"));
      return;
    }
    let templates = documentTemplates;
    if (!templates.length) templates = await loadDocumentTemplates();
    if (!templates.length) {
      toast.error(t("extra.empl.noDocTemplate"));
      return;
    }
    setDocumentsModalOpen(true);
  };

  const handleDepartementSelect = (departement) => {
    setSelectedDepartement(departement);
    setFilterService("");
    setSearchTerm("");
  };

  const resetDepartementFilter = () => setSelectedDepartement(null);

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      if (!formData.registration_number?.trim() || !formData.last_name?.trim()) {
        toast.error(t("extra.empl.requiredFields"));
        return;
      }
      if (!formData.first_name?.trim()) {
        toast.error(t("extra.empl.firstNameRequired"));
        return;
      }
    }
    try {
      setMutating(true);
      const isPostierValue =
        typeof formData.is_postier === "string"
          ? formData.is_postier === "true"
          : Boolean(formData.is_postier);
      const employeeData = {
        registration_number: formData.registration_number?.trim(),
        last_name: formData.last_name?.trim(),
        first_name: formData.first_name?.trim(),
        email: formData.email?.trim(),
        phone_number: formData.phone?.trim(),
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        postal_code: formData.postal_code?.trim() || null,
        job_title: formData.job_name?.trim() || null,
        birth_date: formData.birth_date,
        department_id: formData.department_id ? parseInt(formData.department_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        badge_number: formData.badge_number,
        fingerprint_id: formData.fingerprint_id,
        face_id: formData.face_id,
        card_id: formData.card_id,
        hire_date: formData.hire_date,
        contract_end_date: formData.contract_end_date,
        salary: formData.salary ? parseFloat(formData.salary) : null,
        status: formData.status || "ACTIVE",
        contract_type: formData.contract_type || "PERMANENT",
        role: formData.role || "EMPLOYEE",
        supervisor_id: formData.supervisor_id ? parseInt(formData.supervisor_id) : null,
        coach_id: formData.coach_id ? parseInt(formData.coach_id) : null,
        mentor_id: formData.mentor_id ? parseInt(formData.mentor_id) : null,
        is_postier: isPostierValue,
        regime: formData.regime || null,
        // Gulf/GCC fields
        qid: formData.qid || null,
        is_expatriate: formData.is_expatriate ?? null,
        gosi_number: formData.gosi_number || null,
        iqama_number: formData.iqama_number || null,
        work_permit_number: formData.work_permit_number || null,
        work_permit_expiry: formData.work_permit_expiry || null,
        visa_residence_number: formData.visa_residence_number || null,
        visa_residence_expiry: formData.visa_residence_expiry || null,
        visa_work_number: formData.visa_work_number || null,
        visa_work_expiry: formData.visa_work_expiry || null,
        // Gulf salary structure (contributory base = basic + housing [+ social QA])
        basic_salary: formData.basic_salary !== "" ? parseFloat(formData.basic_salary) : null,
        housing_allowance: formData.housing_allowance !== "" ? parseFloat(formData.housing_allowance) : null,
        transport_allowance: formData.transport_allowance !== "" ? parseFloat(formData.transport_allowance) : null,
        other_allowances: formData.other_allowances !== "" ? parseFloat(formData.other_allowances) : null,
        social_allowance: formData.social_allowance !== "" ? parseFloat(formData.social_allowance) : null,
      };
      Object.keys(employeeData).forEach((key) => {
        if (employeeData[key] === null || employeeData[key] === undefined || employeeData[key] === "") {
          delete employeeData[key];
        }
      });
      if (selectedEmployee) {
        await employeesAPI.updateEmployee(selectedEmployee.id, employeeData);
        toast.success(t("extra.empl.infoUpdated"));
        setShowEditModal(false);
        queryClient.invalidateQueries({ queryKey: ['employees'] });
        if (selectedEmployee?.picture_url) setPhotoPreview(selectedEmployee.picture_url);
      } else {
        await employeesAPI.createEmployee(employeeData);
        toast.success(t("extra.empl.employeeCreated"));
        setShowAddModal(false);
      }
      resetForm();
      setSelectedEmployee(null);
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    } catch (error) {
      let errorMessage = t('employeesPage.toasts.saveError');
      if (error.code === "ECONNABORTED") errorMessage = t("extra.empl.timeout");
      else if (error.response?.data?.detail) errorMessage = error.response.data.detail;
      else if (error.response?.data?.message) errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;
      toast.error(errorMessage);
    } finally {
      setMutating(false);
    }
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    populateForm(employee);
    if (employee?.photo_url || employee?.picture_url) {
      const p = employee.photo_url || employee.picture_url;
      const src = p.startsWith("http")
        ? p
        : p.startsWith("/uploads") || p.startsWith("uploads/")
        ? toUploadUrl(p)
        : p.startsWith("/assets/")
        ? p
        : toUploadUrl(`/uploads/employees/${p}`);
      setPhotoPreview(src);
    } else {
      setPhotoPreview(null);
    }
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (!selectedEmployee) return;
    try {
      setMutating(true);
      await employeesAPI.deleteEmployee(selectedEmployee.id);
      toast.success(t('employeesPage.toasts.deleteSuccess'));
      setShowDeleteDialog(false);
      setSelectedEmployee(null);
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    } catch (error) {
      toast.error(t('employeesPage.toasts.deleteError'));
    } finally {
      setMutating(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t('employeesPage.toasts.invalidImage'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('employeesPage.toasts.imageTooLarge'));
      return;
    }
    try {
      setUploadingPhoto(true);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
      if (selectedEmployee?.id) {
        const resp = await (employeesAPI.uploadEmployeePhoto
          ? employeesAPI.uploadEmployeePhoto(selectedEmployee.id, file)
          : null);
        if (resp?.data?.photo_url) {
          toast.success(t('employeesPage.toasts.photoUploadSuccess'));
          setSelectedEmployee((prev) => (prev ? { ...prev, picture_url: resp.data.photo_url } : prev));
          setPhotoPreview(toUploadUrl(resp.data.photo_url));
          queryClient.invalidateQueries({ queryKey: ['employees'] });
        } else {
          updateField("photo", file);
        }
      } else {
        updateField("photo", file);
        toast.info(t("extra.empl.photoAfterCreate"));
      }
    } catch (error) {
      toast.error(t('employeesPage.toasts.photoUploadError'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const selectionProgress =
    filteredEmployees.length > 0 ? (selectedEmployees.length / filteredEmployees.length) * 100 : 0;

  const statsBaseList = Array.isArray(employees)
    ? employees.filter((emp) => selectedDepartement ? emp.department_id === selectedDepartement.id : true)
    : [];

  const totalAll = statsBaseList.length;
  const activeCountAll = statsBaseList.filter((emp) => emp.status === "ACTIVE").length;
  const suspendedCountAll = statsBaseList.filter((emp) => emp.status === "SUSPENDED").length;
  const inactiveCountAll = statsBaseList.filter((emp) => emp.status === "INACTIVE").length;
  const archivedCountAll = statsBaseList.filter((emp) => emp.status === "ARCHIVED").length;

  return (
    <div className="flex flex-col xl:flex-row gap-6 min-w-0 overflow-x-hidden">
      <div className="w-full xl:w-80 xl:flex-shrink-0">
        <OrganigrammeTree
          onDepartementSelect={handleDepartementSelect}
          selectedDepartementId={selectedDepartement?.id}
          compact={true}
          className="xl:sticky xl:top-1"
        />
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        <EmployeesHeader
          t={t}
          selectedDepartement={selectedDepartement}
          resetDepartementFilter={resetDepartementFilter}
          selectedEmployees={selectedEmployees}
          selectionProgress={selectionProgress}
          setShowImportModal={setShowImportModal}
          setShowAddModal={setShowAddModal}
          showInactive={showInactive}
          setShowInactive={setShowInactive}
          showSuspended={showSuspended}
          setShowSuspended={setShowSuspended}
          showArchived={showArchived}
          setShowArchived={setShowArchived}
        />

        <EmployeeStatsAccordion
          totalAll={totalAll}
          activeCountAll={activeCountAll}
          suspendedCountAll={suspendedCountAll}
          inactiveCountAll={inactiveCountAll}
          archivedCountAll={archivedCountAll}
          selectedDepartement={selectedDepartement}
          t={t}
        />

        <EmployeesSearchCard
          t={t}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedDepartement={selectedDepartement}
        />

        <EmployeeTableCard
          filteredEmployees={filteredEmployees}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
          setShowBulkActions={setShowBulkActions}
          loading={loading}
          selectedDepartement={selectedDepartement}
          hasAdvancedFilters={hasAdvancedFilters}
          handleOpenAdvancedFilters={handleOpenAdvancedFilters}
          handleOpenDocumentsModal={handleOpenDocumentsModal}
          documentTemplatesLoading={documentTemplatesLoading}
          visibleColumns={visibleColumns}
          toggleColumnVisibility={toggleColumnVisibility}
          getVisibleColumnDefs={getVisibleColumnDefs}
          getColumnLabel={getColumnLabel}
          renderCellContent={renderCell}
          t={t}
        />

        <EmployeeFormModal
          open={showAddModal}
          onOpenChange={setShowAddModal}
          mode="add"
          formData={formData}
          updateField={updateField}
          photoPreview={photoPreview}
          selectedEmployee={selectedEmployee}
          uploadingPhoto={uploadingPhoto}
          handlePhotoUpload={handlePhotoUpload}
          departements={departements}
          sites={sites}
          employees={employees}
          loading={loading}
          handleSubmit={handleSubmit}
          onCancel={() => { setShowAddModal(false); resetForm(); }}
        />

        <EmployeeFormModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          mode="edit"
          formData={formData}
          updateField={updateField}
          photoPreview={photoPreview}
          selectedEmployee={selectedEmployee}
          uploadingPhoto={uploadingPhoto}
          handlePhotoUpload={handlePhotoUpload}
          departements={departements}
          sites={sites}
          employees={employees}
          loading={loading}
          handleSubmit={handleSubmit}
          onCancel={() => { setShowEditModal(false); resetForm(); }}
        />

        <AdvancedFiltersDialog
          open={advancedFiltersDialogOpen}
          onOpenChange={setAdvancedFiltersDialogOpen}
          draftAdvancedFilters={draftAdvancedFilters}
          setDraftAdvancedFilters={setDraftAdvancedFilters}
          savedFilters={savedFilters}
          savedFiltersLoading={savedFiltersLoading}
          selectedSavedFilterId={selectedSavedFilterId}
          newFilterName={newFilterName}
          setNewFilterName={setNewFilterName}
          newFilterDescription={newFilterDescription}
          setNewFilterDescription={setNewFilterDescription}
          savingFilter={savingFilter}
          handleSaveFilter={handleSaveFilter}
          handleDeleteFilter={handleDeleteFilter}
          handleSelectSavedFilter={handleSelectSavedFilter}
          handleResetAdvancedFilters={handleResetAdvancedFilters}
          handleApplyAdvancedFilters={handleApplyAdvancedFilters}
          loadSavedFilters={loadSavedFilters}
        />

        <EmployeeDocumentModal
          open={documentsModalOpen}
          onClose={() => setDocumentsModalOpen(false)}
          templates={documentTemplates}
          selectedEmployeeIds={documentTargetIds}
          employeesMap={employeesMap}
          filtersSnapshot={hasAdvancedFilters ? appliedAdvancedFilters : null}
        />

        <EmployeeDeleteDialog
          t={t}
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          selectedEmployee={selectedEmployee}
          loading={loading}
          handleDelete={handleDelete}
        />

        <ImportEmployeesModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportSuccess={() => {
            setShowImportModal(false);
            queryClient.invalidateQueries({ queryKey: ['employees'] });
          }}
        />
      </div>
    </div>
  );
};

export default Employees;
